import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  checkPartyCode,
  getTravelQuoteOptimized,
  getBniTravelQuoteOptimized,
  getTravelPlanPrices,
  getBniTravelPlanPrices,
  getHomeQuote,
  getHomeQuoteOptimized,
  getPersonalCyberQuote,
  getSmartDeviceQuote
} from '../../api/quoteServices';

// Async thunk for checking party code
export const fetchPartyCode = createAsyncThunk(
  'quote/fetchPartyCode',
  async (cpr, { rejectWithValue }) => {
    try {
      const result = await checkPartyCode(cpr);
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for getting travel quote
export const fetchTravelQuote = createAsyncThunk(
  'quote/fetchTravelQuote',
  async ({ formData, options = {} }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const partyCode = state.quote.partyCode || 'FALCON';
      
      // Pass all options including includePassengers to the optimized quote function
      const result = await getTravelQuoteOptimized(formData, partyCode, options);
      return { result, formData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBniTravelQuote = createAsyncThunk(
  'quote/fetchBniTravelQuote',
  async ({ formData, options = {} }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      // Honour an explicit partyCode in formData (e.g. 'FALCON' when buying for someone else).
      const partyCode = formData.partyCode || state.quote.partyCode || 'FALCON';
      const result = await getBniTravelQuoteOptimized(formData, partyCode, options);
      return { result, formData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBniTravelPlanPrices = createAsyncThunk(
  'quote/fetchBniTravelPlanPrices',
  async ({ formData, options = {} }, { rejectWithValue }) => {
    try {
      console.log('Redux - Fetching BNI travel plan prices for:', formData.planType);
      const result = await getBniTravelPlanPrices(formData, options);
      return { result, formData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for getting travel plan prices
export const fetchTravelPlanPrices = createAsyncThunk(
  'quote/fetchTravelPlanPrices',
  async ({ formData, options = {} }, { rejectWithValue }) => {
    try {
      console.log('Redux - Fetching travel plan prices for:', formData.planType);
      const result = await getTravelPlanPrices(formData, options);
      return { result, formData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for getting home quote
export const fetchHomeQuote = createAsyncThunk(
  'quote/fetchHomeQuote',
  async ({ homeFormData, options = {} }, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // CRITICAL: Only use partyCode from Redux - component will wait for it to be fetched
      // Don't use CPR as fallback - let the component handle the wait logic
      const loginInfo = state.loginRegister?.loginTokenInfo;
      const partyCode = state.quote.partyCode || 'FALCON';

      console.log('=== Home Policy Party Code Selection ===');
      console.log('Redux partyCode:', state.quote.partyCode);
      console.log('User CPR:', loginInfo?.cpr);
      console.log('Is logged in:', !!loginInfo);
      console.log('Effective party code:', partyCode);
      console.log('Redux - Home quote options:', options);

      const result = await getHomeQuoteOptimized(homeFormData, partyCode, options);
      return { result, formData: homeFormData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for getting Personal Cyber quote
export const fetchPersonalCyberQuote = createAsyncThunk(
  'quote/fetchPersonalCyberQuote',
  async (personalCyberFormData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      // For someone_else always use FALCON — never the logged-in user's party code.
      // Two callers store the policy-for value under different keys:
      //   contactDetails → policyType ('for_someone_else' | 'for_myself')
      //   planSelection  → policyFor  ('someone_else'     | 'myself')
      const contactForm = personalCyberFormData?.PERSONAL_CYBER_CONTACT_DETAILS_FORM;
      const cyberPolicyFor = contactForm?.policyType || contactForm?.policyFor;
      const isSomeoneElse = cyberPolicyFor === 'for_someone_else' || cyberPolicyFor === 'someone_else';
      const partyCode = isSomeoneElse ? 'FALCON' : (state.quote.partyCode || 'FALCON');

      console.log('Redux - Fetching Personal Cyber quote with party code:', partyCode, '(isSomeoneElse:', isSomeoneElse, ')');
      const result = await getPersonalCyberQuote(personalCyberFormData, partyCode);
      return { result, formData: personalCyberFormData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSmartDeviceQuote = createAsyncThunk(
  'quote/fetchSmartDeviceQuote',
  async (smartDeviceFormData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      // Honour an explicit partyCode in contactDetails (e.g. 'FALCON' when buying for someone else).
      const _sdContactPartyCode = smartDeviceFormData?.SMART_DEVICE_CONTACT_DETAILS_FORM?.partyCode;
      const partyCode = _sdContactPartyCode !== undefined ? _sdContactPartyCode : (state.quote.partyCode || 'FALCON');

      console.log('Redux - Fetching Smart Device quote with party code:', partyCode);
      const result = await getSmartDeviceQuote(smartDeviceFormData, partyCode);
      return { result, formData: smartDeviceFormData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Party code management
  partyCode: null,
  partyExists: false,
  partyLoading: false,
  partyError: null,
  
  // Travel quote management - store quotes by plan for instant switching
  travelQuotes: {}, // Store multiple quotes: { 'silver': {...}, 'gold': {...}, 'platinum': {...} }
  travelQuoteLoading: false,
  travelQuoteError: null,
  travelQuoteLastUpdated: null,
  travelFormSnapshot: null, // Store form data used for last quote
  
  // Travel plan prices management - store prices for all plans
  travelPlanPrices: null, // Store plan prices response from /travel/plan-prices
  travelPlanPricesLoading: false,
  travelPlanPricesError: null,
  travelPlanPricesLastUpdated: null,
  
  // Home quote management
  homeQuote: null,
  homeQuoteLoading: false,
  homeQuoteError: null,
  homeQuoteLastUpdated: null,
  homeFormSnapshot: null,

  // Personal Cyber quote management
  personalCyberQuote: null,
  personalCyberQuoteLoading: false,
  personalCyberQuoteError: null,
  personalCyberQuoteLastUpdated: null,
  personalCyberFormSnapshot: null,

  // Smart Device quote management
  smartDeviceQuote: null,
  smartDeviceQuoteLoading: false,
  smartDeviceQuoteError: null,
  smartDeviceQuoteLastUpdated: null,
  smartDeviceFormSnapshot: null,

  // General quote state
  lastApiCall: null,
  apiCallCount: 0,
  cacheHitCount: 0
};

const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    // Party code actions
    clearPartyCode: (state) => {
      state.partyCode = null;
      state.partyExists = false;
      state.partyError = null;
    },
    
    setPartyCodeManual: (state, action) => {
      state.partyCode = action.payload.PartyCode;
      state.partyExists = action.payload.PartyExists;
    },
    
    // Travel quote actions
    clearTravelQuote: (state) => {
      state.travelQuote = null;
      state.travelQuotes = {}; // Clear all cached quotes
      state.travelQuoteError = null;
      state.travelQuoteLastUpdated = null;
      state.travelFormSnapshot = null;
      // Also clear plan prices when clearing quotes
      state.travelPlanPrices = null;
      state.travelPlanPricesError = null;
      state.travelPlanPricesLastUpdated = null;
    },
    
    setTravelQuoteLoading: (state, action) => {
      state.travelQuoteLoading = action.payload;
    },
    
    setTravelQuoteError: (state, action) => {
      state.travelQuoteError = action.payload;
      state.travelQuoteLoading = false;
    },
    
    // Cache management
    incrementCacheHit: (state) => {
      state.cacheHitCount += 1;
    },
    
    // Home quote actions
    clearHomeQuote: (state) => {
      state.homeQuote = null;
      state.homeQuoteError = null;
      state.homeQuoteLastUpdated = null;
      state.homeFormSnapshot = null;
    },

    setHomeQuoteLoading: (state, action) => {
      state.homeQuoteLoading = action.payload;
    },

    setHomeQuoteError: (state, action) => {
      state.homeQuoteError = action.payload;
      state.homeQuoteLoading = false;
    },

    // Personal Cyber quote actions
    clearPersonalCyberQuote: (state) => {
      state.personalCyberQuote = null;
      state.personalCyberQuoteError = null;
      state.personalCyberQuoteLastUpdated = null;
      state.personalCyberFormSnapshot = null;
    },

    setPersonalCyberQuoteLoading: (state, action) => {
      state.personalCyberQuoteLoading = action.payload;
    },

    setPersonalCyberQuoteError: (state, action) => {
      state.personalCyberQuoteError = action.payload;
      state.personalCyberQuoteLoading = false;
    },
    
    // Clear all quotes
    clearAllQuotes: (state) => {
      state.travelQuote = null;
      state.travelQuotes = {}; // Clear all cached quotes
      state.travelQuoteError = null;
      state.travelQuoteLastUpdated = null;
      state.travelFormSnapshot = null;
      state.homeQuote = null;
      state.homeQuoteError = null;
      state.homeQuoteLastUpdated = null;
      state.homeFormSnapshot = null;
      state.personalCyberQuote = null;
      state.personalCyberQuoteError = null;
      state.personalCyberQuoteLastUpdated = null;
      state.personalCyberFormSnapshot = null;
      state.smartDeviceQuote = null;
      state.smartDeviceQuoteError = null;
      state.smartDeviceQuoteLastUpdated = null;
      state.smartDeviceFormSnapshot = null;
    },
    
    // Reset entire state
    resetQuoteState: () => initialState
  },
  
  extraReducers: (builder) => {
    // Party code async actions
    builder
      .addCase(fetchPartyCode.pending, (state) => {
        state.partyLoading = true;
        state.partyError = null;
      })
      .addCase(fetchPartyCode.fulfilled, (state, action) => {
        state.partyLoading = false;
        const { data, fallback } = action.payload;
        state.partyCode = data.PartyCode;
        state.partyExists = data.PartyExists;
        if (fallback) {
          state.partyError = 'Using guest mode (FALCON)';
        }
      })
      .addCase(fetchPartyCode.rejected, (state, action) => {
        state.partyLoading = false;
        state.partyError = action.payload;
        // Fallback to FALCON for guests
        state.partyCode = 'FALCON';
        state.partyExists = false;
      })
      
    // Travel quote async actions
    builder
      .addCase(fetchTravelQuote.pending, (state) => {
        state.travelQuoteLoading = true;
        state.travelQuoteError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchTravelQuote.fulfilled, (state, action) => {
        state.travelQuoteLoading = false;
        const { result, formData } = action.payload;
        
        if (result.success) {
          // Store quote by plan + plan type for proper family/individual separation
          const planId = formData.selectedPlan;
          const planType = formData.planType?.toLowerCase() || 'individual';
          
          if (planId) {
            // Create composite key: planId + planType (e.g., "gold-family", "gold-individual")
            const cacheKey = `${planId}-${planType}`;
            
            state.travelQuotes[cacheKey] = {
              data: result.data,
              lastUpdated: Date.now(),
              formData: formData
            };
            
            // Also store under simple planId for backward compatibility
            state.travelQuotes[planId] = {
              data: result.data,
              lastUpdated: Date.now(),
              formData: formData
            };
            
            console.log(`✅ CACHED QUOTE: ${cacheKey} (${planType} plan) stored in Redux`);
          }
          
          // Keep the old structure for backward compatibility
          state.travelQuote = result.data;
          state.travelQuoteLastUpdated = Date.now();
          state.travelFormSnapshot = formData;
          state.travelQuoteError = null;
        } else {
          state.travelQuoteError = result.error;
          // Keep existing quote if API fails
          if (!state.travelQuote) {
            state.travelQuote = null;
          }
        }
      })
      .addCase(fetchTravelQuote.rejected, (state, action) => {
        state.travelQuoteLoading = false;
        state.travelQuoteError = action.payload;
      })

    builder
      .addCase(fetchBniTravelQuote.pending, (state) => {
        state.travelQuoteLoading = true;
        state.travelQuoteError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchBniTravelQuote.fulfilled, (state, action) => {
        state.travelQuoteLoading = false;
        const { result, formData } = action.payload;

        if (result.success) {
          const planId = formData.selectedPlan;
          const planType = formData.planType?.toLowerCase() || 'individual';

          if (planId) {
            const cacheKey = `${planId}-${planType}`;
            state.travelQuotes[cacheKey] = {
              data: result.data,
              lastUpdated: Date.now(),
              formData: formData
            };
            state.travelQuotes[planId] = {
              data: result.data,
              lastUpdated: Date.now(),
              formData: formData
            };
          }

          state.travelQuote = result.data;
          state.travelQuoteLastUpdated = Date.now();
          state.travelFormSnapshot = formData;
          state.travelQuoteError = null;
        } else {
          state.travelQuoteError = result.error;
          if (!state.travelQuote) {
            state.travelQuote = null;
          }
        }
      })
      .addCase(fetchBniTravelQuote.rejected, (state, action) => {
        state.travelQuoteLoading = false;
        state.travelQuoteError = action.payload;
      })

    // Travel plan prices async actions
    builder
      .addCase(fetchTravelPlanPrices.pending, (state) => {
        state.travelPlanPricesLoading = true;
        state.travelPlanPricesError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchTravelPlanPrices.fulfilled, (state, action) => {
        state.travelPlanPricesLoading = false;
        const { result, formData } = action.payload;
        
        if (result.success) {
          state.travelPlanPrices = result.data;
          state.travelPlanPricesLastUpdated = Date.now();
          console.log('✅ PLAN PRICES: Stored in Redux for', formData.planType);
        } else {
          state.travelPlanPricesError = result.error || 'Failed to get plan prices';
          console.error('❌ PLAN PRICES: Failed', result.error);
        }
      })
      .addCase(fetchTravelPlanPrices.rejected, (state, action) => {
        state.travelPlanPricesLoading = false;
        state.travelPlanPricesError = action.payload;
        state.travelPlanPrices = null;
      })

    builder
      .addCase(fetchBniTravelPlanPrices.pending, (state) => {
        state.travelPlanPricesLoading = true;
        state.travelPlanPricesError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchBniTravelPlanPrices.fulfilled, (state, action) => {
        state.travelPlanPricesLoading = false;
        const { result, formData } = action.payload;

        if (result.success) {
          state.travelPlanPrices = result.data;
          state.travelPlanPricesLastUpdated = Date.now();
        } else {
          state.travelPlanPricesError = result.error || 'Failed to get BNI plan prices';
        }
      })
      .addCase(fetchBniTravelPlanPrices.rejected, (state, action) => {
        state.travelPlanPricesLoading = false;
        state.travelPlanPricesError = action.payload;
        state.travelPlanPrices = null;
      })

    // Home quote async actions
    builder
      .addCase(fetchHomeQuote.pending, (state) => {
        state.homeQuoteLoading = true;
        state.homeQuoteError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchHomeQuote.fulfilled, (state, action) => {
        state.homeQuoteLoading = false;
        const { result, formData } = action.payload;

        if (result.success) {
          state.homeQuote = result.data;
          state.homeQuoteLastUpdated = Date.now();
          state.homeFormSnapshot = formData;
          state.homeQuoteError = null;
          console.log('✅ HOME QUOTE: Stored in Redux', result.data);
        } else {
          state.homeQuoteError = result.error;
          state.homeQuote = null;
        }
      })
      .addCase(fetchHomeQuote.rejected, (state, action) => {
        state.homeQuoteLoading = false;
        state.homeQuoteError = action.payload;
        state.homeQuote = null;
      })

    // Personal Cyber quote async actions
    builder
      .addCase(fetchPersonalCyberQuote.pending, (state) => {
        state.personalCyberQuoteLoading = true;
        state.personalCyberQuoteError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchPersonalCyberQuote.fulfilled, (state, action) => {
        state.personalCyberQuoteLoading = false;
        const { result, formData } = action.payload;

        if (result.success) {
          state.personalCyberQuote = result.data;
          state.personalCyberQuoteLastUpdated = Date.now();
          state.personalCyberFormSnapshot = formData;
          state.personalCyberQuoteError = null;
          console.log('✅ PERSONAL CYBER QUOTE: Stored in Redux', result.data);
        } else {
          state.personalCyberQuoteError = result.error;
          state.personalCyberQuote = null;
        }
      })
      .addCase(fetchPersonalCyberQuote.rejected, (state, action) => {
        state.personalCyberQuoteLoading = false;
        state.personalCyberQuoteError = action.payload;
        state.personalCyberQuote = null;
      })
      // Smart Device Quote
      .addCase(fetchSmartDeviceQuote.pending, (state) => {
        state.smartDeviceQuoteLoading = true;
        state.smartDeviceQuoteError = null;
        state.apiCallCount += 1;
        state.lastApiCall = Date.now();
      })
      .addCase(fetchSmartDeviceQuote.fulfilled, (state, action) => {
        state.smartDeviceQuoteLoading = false;
        const { result, formData } = action.payload;

        if (result.success) {
          state.smartDeviceQuote = result.data;
          state.smartDeviceQuoteLastUpdated = Date.now();
          state.smartDeviceFormSnapshot = formData;
          state.smartDeviceQuoteError = null;
          console.log('✅ SMART DEVICE QUOTE: Stored in Redux', result.data);
        } else {
          state.smartDeviceQuoteError = result.error;
          state.smartDeviceQuote = null;
        }
      })
      .addCase(fetchSmartDeviceQuote.rejected, (state, action) => {
        state.smartDeviceQuoteLoading = false;
        state.smartDeviceQuoteError = action.payload;
        state.smartDeviceQuote = null;
      });
  }
});

// Action creators
export const {
  clearPartyCode,
  setPartyCodeManual,
  clearTravelQuote,
  setTravelQuoteLoading,
  setTravelQuoteError,
  clearHomeQuote,
  setHomeQuoteLoading,
  setHomeQuoteError,
  clearPersonalCyberQuote,
  setPersonalCyberQuoteLoading,
  setPersonalCyberQuoteError,
  incrementCacheHit,
  clearAllQuotes,
  resetQuoteState
} = quoteSlice.actions;

// Selectors
export const selectPartyCode = (state) => state.quote.partyCode;
export const selectPartyExists = (state) => state.quote.partyExists;
export const selectPartyLoading = (state) => state.quote.partyLoading;
export const selectPartyError = (state) => state.quote.partyError;

export const selectTravelQuote = (state) => state.quote.travelQuote;
export const selectTravelQuoteLoading = (state) => state.quote.travelQuoteLoading;
export const selectTravelQuoteError = (state) => state.quote.travelQuoteError;
export const selectTravelQuoteLastUpdated = (state) => state.quote.travelQuoteLastUpdated;
export const selectTravelFormSnapshot = (state) => state.quote.travelFormSnapshot;

export const selectHomeQuote = (state) => state.quote.homeQuote;
export const selectHomeQuoteLoading = (state) => state.quote.homeQuoteLoading;
export const selectHomeQuoteError = (state) => state.quote.homeQuoteError;
export const selectHomeQuoteLastUpdated = (state) => state.quote.homeQuoteLastUpdated;
export const selectHomeFormSnapshot = (state) => state.quote.homeFormSnapshot;

export const selectPersonalCyberQuote = (state) => state.quote.personalCyberQuote;
export const selectPersonalCyberQuoteLoading = (state) => state.quote.personalCyberQuoteLoading;
export const selectPersonalCyberQuoteError = (state) => state.quote.personalCyberQuoteError;
export const selectPersonalCyberQuoteLastUpdated = (state) => state.quote.personalCyberQuoteLastUpdated;
export const selectPersonalCyberFormSnapshot = (state) => state.quote.personalCyberFormSnapshot;

// Smart Device quote selectors
export const selectSmartDeviceQuote = (state) => state.quote.smartDeviceQuote;
export const selectSmartDeviceQuoteLoading = (state) => state.quote.smartDeviceQuoteLoading;
export const selectSmartDeviceQuoteError = (state) => state.quote.smartDeviceQuoteError;
export const selectSmartDeviceQuoteLastUpdated = (state) => state.quote.smartDeviceQuoteLastUpdated;
export const selectSmartDeviceFormSnapshot = (state) => state.quote.smartDeviceFormSnapshot;

export const selectQuoteStats = (state) => ({
  apiCallCount: state.quote.apiCallCount,
  cacheHitCount: state.quote.cacheHitCount,
  lastApiCall: state.quote.lastApiCall
});

// New selector for cached quotes by plan
export const selectTravelQuoteByPlan = (planId) => (state) => {
  return state.quote.travelQuotes[planId] || null;
};

export const selectAllTravelQuotes = (state) => state.quote.travelQuotes;

// Travel plan prices selectors
export const selectTravelPlanPrices = (state) => state.quote.travelPlanPrices;
export const selectTravelPlanPricesLoading = (state) => state.quote.travelPlanPricesLoading;
export const selectTravelPlanPricesError = (state) => state.quote.travelPlanPricesError;
export const selectTravelPlanPricesLastUpdated = (state) => state.quote.travelPlanPricesLastUpdated;

// Helper selectors
export const selectIsQuoteStale = (maxAgeMinutes = 5) => (state) => {
  const lastUpdated = state.quote.travelQuoteLastUpdated;
  if (!lastUpdated) return true;
  
  const ageMinutes = (Date.now() - lastUpdated) / (1000 * 60);
  return ageMinutes > maxAgeMinutes;
};

export const selectShouldRefreshQuote = (formData) => (state) => {
  const snapshot = state.quote.travelFormSnapshot;
  if (!snapshot || !state.quote.travelQuote) return true;
  
  // Compare critical fields that affect pricing
  const criticalFields = [
    'selectedPlan',
    'departureDate', 
    'returnDate',
    'addOns.terrorism',
    'addOns.waterSport',
    'addOns.winterSport'
  ];
  
  return criticalFields.some(field => {
    const getValue = (obj, path) => path.split('.').reduce((o, p) => o?.[p], obj);
    return getValue(formData, field) !== getValue(snapshot, field);
  });
};

export default quoteSlice.reducer;